import { useEffect, useState, type ComponentType, type ReactNode } from "react";

type MotionModule = typeof import("framer-motion");
type ProviderComponent = ComponentType<{ children: ReactNode }>;

/**
 * Wraps the app tree with framer-motion's LazyMotion provider, but fires the
 * chunk fetch during browser idle — not during React's initial render — so
 * motion-vendor stays entirely off the LCP network path. The first render
 * returns children unwrapped; `m.*` components queue their first frame until
 * the provider swaps in (invisible, since no motion is on the LCP path).
 */
export const DeferredMotionProvider = ({ children }: { children: ReactNode }) => {
  const [Provider, setProvider] = useState<ProviderComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void import("framer-motion").then((mod: MotionModule) => {
        if (cancelled) return;
        const Wrap: ProviderComponent = ({ children: c }) => (
          <mod.LazyMotion features={mod.domAnimation} strict>
            {c}
          </mod.LazyMotion>
        );
        setProvider(() => Wrap);
      });
    };

    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as IdleWindow;
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(load, { timeout: 2000 });
      return () => {
        cancelled = true;
        w.cancelIdleCallback?.(id);
      };
    }
    const timer = window.setTimeout(load, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  if (!Provider) return <>{children}</>;
  return <Provider>{children}</Provider>;
};
